/**
 * ==========================================================================
 *  AAMUPALA - the toll at the muurikka
 * ==========================================================================
 *  Breakfast is not an offer any more. The juhlanavetta's door has a man from
 *  the ruokatiimi standing in it, and he does not move until Kimmo has three
 *  helpings of meat on his plate. So the route now runs out to the pan on the
 *  kivetys, stops there, and buys its way back in.
 *
 *  What happens at the pan is a three-card game played with a lasta. The cook
 *  scrambles the plate for about a second, three boxes come up - one meat and
 *  two vegetables, drawn fresh out of the pans below and laid down in an order
 *  that is different every round - and there are 0.75, 0.65 and 0.55 seconds to
 *  hit the one with the meat in it before he sweeps it all back together and
 *  does it again. Miss, and you have simply lost the round. Hit the wrong one
 *  and Kimmo eats it, which is worse: he does not keep vegetables down, and
 *  `vomitDuration` is how long that takes.
 *
 *  ------------------------------------------------------------------------
 *   WHY THIS IS RANDOM AND STILL VERIFIABLE
 *  ------------------------------------------------------------------------
 *  Exactly the same contract as the beer, and it is worth reading
 *  `src/config/beer.ts` for the long version. The hand - what is in the boxes
 *  and which box is which - is rolled from two numbers and no others: the
 *  simulation step Kimmo *arrived* at the pan on, and which round it is.
 *
 *    For the player that is real randomness. Nobody arrives at a grill on a
 *    chosen 1/120th of a second, so the same route walked the same way deals
 *    a different hand every attempt and the minigame cannot be reduced to
 *    "press 2, press 2, press 2".
 *
 *    For the server it is not random at all. It re-simulates the recorded
 *    inputs, Kimmo arrives on the same step number he arrived on live, and the
 *    same three hands come back out. No hidden state, no wall clock, no
 *    Math.random().
 *
 *  ------------------------------------------------------------------------
 *   ALL OF THIS IS HASHED
 *  ------------------------------------------------------------------------
 *  Every number in here decides how long a run takes, so every number in here
 *  is inside `simulationFingerprint()`. Nudging `mixDuration` by a frame is a
 *  scoreboard reset, the same as nudging `PLAYER.maxSpeed`. See
 *  `src/config/CLAUDE.md`.
 *
 *  The two lists below are the exception, and deliberately so: what a run can
 *  be timed against is *which box* the meat is in, and `rollBoxes` rolls that
 *  before it has looked at either list. A sixth vegetable is a new picture on
 *  a screen, not a new number in the physics, and it may be added without
 *  retiring a single leaderboard time.
 * ==========================================================================
 */

/**
 * The meat, of which exactly one is out per round and which is always the box
 * to press.
 *
 * Three of them rather than one, because the box that is worth pressing has to
 * be recognised rather than remembered. A single makkara every round is a
 * silhouette you learn once and then find by shape alone; three different
 * meats mean the question the eye is answering is "is that meat", which is the
 * question the two vegetables beside it are asked to make hard.
 *
 * Which one comes up is `rollBoxes`' business, and it has one rule: never the
 * same meat twice running. See `src/world/Aamupala.ts`.
 */
export const MEATS = ['makkara', 'pihvi', 'pulled-pork'] as const;

/**
 * And what the ruokatiimi puts in the other two boxes. Two of these every
 * round, never the same one twice in a hand, and otherwise a fresh draw every
 * time - unlike the meats, there is nothing to be gained by spacing them out.
 *
 * Five for two slots is what keeps a round from being read off one box. With
 * two vegetables and one of them always out, a runner learns the pair; with
 * five, the two beside the meat are a different pair most rounds and there is
 * nothing to do but look at all three.
 */
export const VEGES = ['parsakaali', 'falafel', 'tofu', 'harkis', 'munakoiso'] as const;

export type Meat = (typeof MEATS)[number];
export type Vege = (typeof VEGES)[number];
export type Food = Meat | Vege;

/** Everything that can come up in a box, which is only ever used to draw them. */
export const FOODS: readonly Food[] = [...MEATS, ...VEGES];

/**
 * The half of the plate that stays down. Everything else Kimmo eats comes
 * straight back up, and `vomitDuration` is what that costs.
 */
export function isMeat(food: Food): food is Meat {
  return (MEATS as readonly string[]).includes(food);
}

export const AAMUPALA = {
  /**
   * How many helpings of meat the plate needs before the door is Kimmo's
   * problem rather than Mäysä's.
   *
   * Three, because two is one mistake away from a formality and four is a
   * minigame you resent. At the cadence below a clean three is a shade over
   * three seconds, which is roughly what the detour out to the pan and back
   * costs on its own - so the toll is half running and half reacting rather
   * than mostly one of them.
   */
  servings: 3,

  /**
   * How close Kimmo has to be to the middle of the plate to be dealt to, in
   * metres.
   *
   * Distance and nothing else - no look cone, unlike every other interaction
   * in the game. `INTERACT.minLookDot` exists so that a door you did not mean
   * to open stays shut; there is nothing else within nine metres of this pan
   * to confuse it with, and a reaction test that also asks you to keep the
   * crosshair on a griddle is a test of the mouse rather than of the hands.
   * You stand at the grill, the cook deals. Where you are looking is yours.
   */
  reach: 1.9,

  /* ----------------------------------------------------------- the round -- */

  /**
   * Seconds the lasta is going and the boxes say nothing. A press during this
   * buys nothing: there is not yet anything to press.
   */
  mixDuration: 1.0,
  /**
   * Seconds the boxes stand still for the first helping. Later helpings lose
   * `pickReductionPerServing` for each meat already on the plate.
   *
   * Three quarters of a second, and the two numbers it is *not* are the whole
   * argument for it. A hand is about a quarter of a second behind an eye
   * before it has decided anything, so what this really sets is how long there
   * is to be *sure*:
   *
   *   1.0 s  - a formality. The boxes were a thing you waited through rather
   *            than a thing you read, and nobody ever ate a falafel.
   *   0.5 s  - the clock does the work. You miss the window, and missing is
   *            the cheap failure - which puts the difficulty in the wrong
   *            place, because a toll you fail by being slow teaches nothing
   *            about the three boxes it is made of.
   *   0.75 s - long enough to press and not always long enough to be right,
   *            which makes the *falafel* the thing that catches people. That
   *            is the failure this minigame is built around and the expensive
   *            one; see `vomitDuration`.
   *
   * A successful choice ends the window immediately. A missed choice waits
   * out the current helping's whole window before a fresh mix begins.
   */
  pickDuration: 0.75,
  /** Shorten the next choice by 100 ms per successful helping, not per retry. */
  pickReductionPerServing: 0.1,

  /**
   * Seconds lost to a mouthful of vegetables, from the first heave to the pan
   * starting up again.
   *
   * Priced above a whole round rather than at a fraction of one. A miss - no
   * key at all - costs `mixDuration + pickDuration`, so a wrong box has to cost
   * more than that or the fast line would be to mash a key and take whatever
   * came, and the boxes would be scenery. It clears that bar comfortably: a
   * mistake is this heave *plus* the fresh mix that follows it, 2.6 s against
   * a miss's 1.75.
   *
   * The margin is what has to survive a re-tune of the window, and it moves
   * the wrong way as the window shortens - a guess gets more tempting exactly
   * when there is less time to do better. `Aamupala.test.ts` asserts the
   * inequality rather than trusting this paragraph.
   */
  vomitDuration: 1.6,

  /**
   * Mixed into every round's seed. Changing it re-deals every hand in the game
   * and, like any other number in this file, retires the leaderboard.
   */
  seed: 0x4d415953,
} as const;
